/**
 * Last-known-good cache for read-only screens.
 *
 * The scanner was built offline-capable because a queue at a door cannot stall.
 * The Today view was not — same venue, same unreliable wifi, and if the
 * connection drops mid-event the day-of screen simply dies. Half-solving that
 * problem is worse than not solving it, because people learn to trust the
 * screen and then it fails at the worst moment.
 *
 * This is deliberately narrow: it caches responses so a screen can still render
 * when the network is gone, and it always tells the caller how old the data is.
 * Silently showing stale information as though it were live would be a worse
 * failure than showing nothing.
 */

const PREFIX = "ivem.cache.v1:";

function key(name) {
  return `${PREFIX}${name}`;
}

/** Store a successful response alongside the time it was fetched. */
export function put(name, data) {
  try {
    localStorage.setItem(key(name), JSON.stringify({ at: Date.now(), data }));
    return true;
  } catch {
    // A full or unavailable localStorage must never break the screen.
    return false;
  }
}

/**
 * Read a cached response.
 * Returns `{ data, at, ageMs }`, or null when nothing usable is stored.
 */
export function get(name) {
  try {
    const raw = localStorage.getItem(key(name));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !("data" in parsed)) return null;
    return { data: parsed.data, at: parsed.at, ageMs: Date.now() - (parsed.at || 0) };
  } catch {
    return null;
  }
}

export function drop(name) {
  try {
    localStorage.removeItem(key(name));
  } catch {
    /* nothing to do */
  }
}

/** Clear every cached response. Used on sign-out. */
export function clearAll() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* nothing to do */
  }
}

/** "3 minutes ago" — plain words, because this is shown during an event. */
export function describeAge(ms) {
  if (ms == null) return "";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "moments ago";
  if (mins === 1) return "1 minute ago";
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours === 1) return "1 hour ago";
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

/**
 * Fetch with a cached fallback.
 *
 * `fetcher` should return the response data. On success the result is cached
 * and returned as fresh. On failure the cached copy is returned as stale, with
 * its age — and if there is no cache, the error propagates so the screen can
 * show a real error rather than an empty one pretending to be data.
 */
export async function fetchWithCache(name, fetcher) {
  try {
    const data = await fetcher();
    put(name, data);
    return { data, stale: false, ageMs: 0 };
  } catch (err) {
    const cached = get(name);
    if (cached) {
      return { data: cached.data, stale: true, ageMs: cached.ageMs, error: err };
    }
    throw err;
  }
}
